import type { ReactNode } from 'react';
import { useSidebarPinState } from '@backstage/core-components';

import styles from './sidebar-scroll-area.module.css';

/** @public */
export interface SidebarScrollAreaProps {
  /** The primary menu. It grows to fill the area above the utility block. */
  children: ReactNode;
  /**
   * The utility menu, pinned to the bottom of the area when there is room and
   * scrolling together with the primary menu when there is not. Without it,
   * the primary menu fills the whole area.
   */
  utility?: ReactNode;
}

/**
 * Scroll container for the primary and utility slots of the sidebar.
 *
 * @remarks
 * Place it inside a `SidebarGroup`, never around one: core's mobile bottom bar
 * only turns direct `SidebarGroup` children of `Sidebar` into tabs. On desktop
 * a group is a fragment, so this area is a direct child of the drawer and
 * shrinks between the fixed brand and control slots. On mobile it is a plain
 * block inside core's menu overlay, which scrolls on its own: the primary menu
 * with the utility block docked to the bottom, inset so the list lines up
 * with the overlay title.
 *
 * @public
 */
export const SidebarScrollArea = ({
  children,
  utility,
}: SidebarScrollAreaProps) => {
  const { isMobile } = useSidebarPinState();

  const utilityBlock = utility !== undefined && utility !== null && (
    <div className={styles.utility}>{utility}</div>
  );

  if (isMobile) {
    return (
      <div className={styles.mobile}>
        {children}
        {utilityBlock}
      </div>
    );
  }

  return (
    <div className={styles.area}>
      <div className={styles.primary}>{children}</div>
      {utilityBlock}
    </div>
  );
};
